import { useCallback, useEffect, useRef, useState } from "react";
import {
  type AgentReport, type ReportAttempt, archiveAgentReport, getReportAttempt,
  listAgentReports, reportIsRunning,
} from "@/lib/api/agentReports";
import { backendErrorMessage } from "@/lib/backendErrorMessage";

export function useAgentReports(projectId?: string, versionId?: string, onGenerated?: (attempt: ReportAttempt) => Promise<void>) {
  const [reports, setReports] = useState<AgentReport[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(projectId));
  const [busy, setBusy] = useState(false);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const locked = useRef(false);
  const generated = useRef(onGenerated);
  generated.current = onGenerated;
  const seenActive = useRef(new Set<string>());
  const refresh = useCallback(() => setRefreshIndex(index => index + 1), []);
  const upsert = useCallback((report: AgentReport) => {
    if (reportIsRunning(report)) seenActive.current.add(report.latest_attempt!.id);
    setReports(current => current.some(item => item.id === report.id)
      ? current.map(item => item.id === report.id ? report : item) : [...current, report]);
  }, []);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const notifyCompleted = async (current: AgentReport[]) => {
      for (const report of current) {
        if (cancelled) return;
        const attempt = report.latest_attempt;
        if (attempt && seenActive.current.has(attempt.id) && (attempt.status === "completed" || attempt.status === "failed")) {
          if (attempt.outcome === "generated") await generated.current?.(attempt);
          seenActive.current.delete(attempt.id);
        }
      }
    };
    const tick = async () => {
      let delay: number | undefined;
      try {
        let current = await listAgentReports(projectId, versionId);
        if (cancelled) return;
        await notifyCompleted(current);
        if (cancelled) return;
        const running = current.filter(reportIsRunning);
        if (running.length) {
          setReports(current);
          for (const report of running) {
            seenActive.current.add(report.latest_attempt!.id);
            const attempt = await getReportAttempt(projectId, report.latest_attempt!);
            if (cancelled) return;
            if (attempt.status === "completed" && attempt.outcome === "generated") {
              await generated.current?.(attempt);
              seenActive.current.delete(attempt.id);
            }
          }
          if (cancelled) return;
          current = await listAgentReports(projectId, versionId);
          await notifyCompleted(current);
        }
        if (current.some(reportIsRunning)) delay = 2_000;
        if (!cancelled) { setReports(current); setLoadError(null); }
      } catch (failure) {
        delay = 5_000;
        if (!cancelled) setLoadError(backendErrorMessage(failure, "Report status could not be loaded. Reconnecting…"));
      } finally {
        if (!cancelled) {
          setLoading(false);
          if (delay !== undefined && document.visibilityState !== "hidden") timer = setTimeout(tick, delay);
        }
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") clearTimeout(timer);
      else refresh();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [projectId, versionId, refreshIndex, refresh]);

  const archive = async (report: AgentReport) => {
    if (!projectId || locked.current) return false;
    locked.current = true; setBusy(true); setError(null);
    try {
      await archiveAgentReport(projectId, report.id);
      setReports(current => current.filter(item => item.id !== report.id));
      return true;
    } catch (failure) {
      setError(backendErrorMessage(failure, "The report could not be removed."));
      return false;
    } finally { locked.current = false; setBusy(false); refresh(); }
  };
  return { reports, error: error ?? loadError, loading, busy, refresh, upsert, archive };
}
